import { Component, computed, effect, inject, input, signal } from "@angular/core";
import { TranslatePipe } from "@ngx-translate/core";

import { PercentageValuePipe, TranslateTokenPipe } from "../../../../core/pipes";
import { AdaaHelper } from "../../../../core/utils";
import { Constants } from "../../../constants/constants";
import type { GovEnablerDirectionsModelType } from "../../../models";
import { FilesApiService } from "../../../services";
import { CountUpComponent } from "../../count-up";

@Component({
  selector: "adaa-national-strategies-perf-card",
  templateUrl: "./national-strategies-perf-card.html",
  styleUrl: "./national-strategies-perf-card.scss",
  imports: [TranslateTokenPipe, TranslatePipe, PercentageValuePipe, CountUpComponent],
})
export class NationalStrategiesPerfCardComponent {
  private readonly _fileService = inject(FilesApiService);

  data = input.required<GovEnablerDirectionsModelType>();

  logo = signal<string | undefined>(undefined);

  initials = computed(() => this._initials(AdaaHelper.getItemValueByToken(this.data(), "name")));

  metrics = computed(() => [
    this._metric(
      "landingPage.entity_page.overall_performance",
      this.data().visionAchievement,
      this.data().visionAchievementColor
    ),
    this._metric("landingPage.entity_page.annual_performance", this.data().performance, this.data().performanceColor),
    this._metric("box_status.initiative_progress", this.data().initiativeProgress, this.data().initiativeProgressColor),
  ]);

  readonly defaultColor = Constants.COLOR_CODES[Constants.CONSTANT_COLORS.EVALUATION_GRAY];
  readonly valueFontSize = "0.9375rem";

  constructor() {
    effect(() => {
      this._downloadFile();
    });
  }

  private _metric(label: string, value: number | undefined, colorCode: number | undefined) {
    return {
      label,
      value,
      color: this._evaluationColor(value, colorCode),
      progress: this._progress(value),
    };
  }

  private _evaluationColor(value: number | undefined, colorCode: number | undefined) {
    if (!AdaaHelper.isDefined(value) || !AdaaHelper.isDefined(colorCode)) return this.defaultColor;
    return Constants.COLOR_CODES[colorCode];
  }

  private _progress(value: number | undefined) {
    if (!AdaaHelper.isDefined(value)) return 0;
    return Math.max(0, Math.min(100, value));
  }

  private _initials(name: string) {
    if (!AdaaHelper.isDefinedAndNotEmpty(name)) return "";

    return name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join("");
  }

  private _downloadFile() {
    if (!this.data()?.entityLogo) return;

    this._fileService.download(this.data().entityLogo).subscribe({
      next: (blob) => {
        const dataUrl = window.URL.createObjectURL(blob);
        this.logo.update(() => dataUrl);
      },
    });
  }
}
